import {
  COMPANY_NAME,
  TAGLINE,
  COLORS_RGB,
  LAYOUT_MM,
  LOGO_BLACK,
  ROBOT_IMAGE,
  footerContactLine,
} from './branding';

let cachedAssets = null;

async function toDataUrl(src) {
  if (!src) return null;
  if (typeof src === 'string' && src.startsWith('data:')) return src;
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Resolve black logo + robot to data URLs for jsPDF. */
export async function getLetterPadAssetsDataUrls() {
  if (cachedAssets) return cachedAssets;
  const [logoDataUrl, robotDataUrl] = await Promise.all([
    toDataUrl(LOGO_BLACK),
    toDataUrl(ROBOT_IMAGE),
  ]);
  cachedAssets = { logoDataUrl, robotDataUrl };
  return cachedAssets;
}

/** @deprecated prefer getLetterPadAssetsDataUrls */
export async function getLetterPadLogoDataUrl() {
  const { logoDataUrl } = await getLetterPadAssetsDataUrls();
  return logoDataUrl;
}

function lerp(a, b, t) {
  return Math.round(a + (b - a) * t);
}

function gradientRgb(t) {
  // Violet–pink brand: blue → violet → pink
  const { blue, violet, pink } = COLORS_RGB;
  if (t <= 0.5) {
    const u = t / 0.5;
    return [lerp(blue[0], violet[0], u), lerp(blue[1], violet[1], u), lerp(blue[2], violet[2], u)];
  }
  const u = (t - 0.5) / 0.5;
  return [lerp(violet[0], pink[0], u), lerp(violet[1], pink[1], u), lerp(violet[2], pink[2], u)];
}

/** Smooth horizontal gradient + soft white wave (no color blocks). */
function drawSmoothGradientHeader(pdf, yTop, bandHeight) {
  const { pageWidth } = LAYOUT_MM;
  const strips = 42;
  const stripW = pageWidth / strips;
  for (let i = 0; i < strips; i++) {
    pdf.setFillColor(...gradientRgb(i / (strips - 1)));
    pdf.rect(i * stripW, yTop, stripW + 0.4, bandHeight, 'F');
  }

  // Soft white wave along bottom
  pdf.setFillColor(...COLORS_RGB.white);
  const waveY = yTop + bandHeight - 1;
  const steps = 14;
  for (let i = 0; i < steps; i++) {
    const x0 = (pageWidth / steps) * i;
    const x1 = (pageWidth / steps) * (i + 1);
    const mid = (x0 + x1) / 2;
    const amp = i % 2 === 0 ? -3.2 : 2.4;
    pdf.triangle(x0, waveY + 10, mid, waveY + amp, x1, waveY + 10, 'F');
  }
  pdf.rect(0, waveY + 3, pageWidth, 16, 'F');
}

function drawHeader(pdf, { logoDataUrl = null, robotDataUrl = null } = {}) {
  const { pageWidth, headerHeight, sideMargin } = LAYOUT_MM;
  const bandHeight = 40;
  drawSmoothGradientHeader(pdf, 0, bandHeight);

  // Left: white badge + black logo
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(sideMargin, 6, 36, 14, 3, 3, 'F');
  if (logoDataUrl) {
    try {
      pdf.addImage(logoDataUrl, 'PNG', sideMargin + 3, 7.5, 30, 11);
    } catch {
      // ignore
    }
  }

  // Right: Spotbot Lite — large, stays in band
  if (robotDataUrl) {
    try {
      const robotSize = 34;
      pdf.addImage(
        robotDataUrl,
        'PNG',
        pageWidth - sideMargin - robotSize + 2,
        2,
        robotSize,
        robotSize
      );
    } catch {
      // ignore
    }
  }

  // Center title + tagline
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(...COLORS_RGB.white);
  pdf.text(COMPANY_NAME, pageWidth / 2, 14, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6.5);
  pdf.setTextColor(...COLORS_RGB.white);
  const tagLines = pdf.splitTextToSize(TAGLINE, pageWidth - sideMargin * 2 - 70);
  pdf.text(tagLines, pageWidth / 2, 22, { align: 'center' });

  pdf.setDrawColor(...COLORS_RGB.violet);
  pdf.setLineWidth(0.35);
  pdf.line(sideMargin, headerHeight - 4, pageWidth - sideMargin, headerHeight - 4);
}

/**
 * Draw a document-specific title block under the letter pad.
 * @returns {number} y after the title block
 */
export function drawDocumentTitle(pdf, { title, subtitle = '', meta = '' } = {}) {
  const { pageWidth, sideMargin, contentTop } = LAYOUT_MM;
  let y = contentTop;

  if (title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS_RGB.ink);
    pdf.text(title, pageWidth / 2, y, { align: 'center' });
    y += 7;
  }
  if (subtitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS_RGB.slate);
    pdf.text(subtitle, pageWidth / 2, y, { align: 'center' });
    y += 6;
  }
  if (meta) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(148, 163, 184);
    const lines = pdf.splitTextToSize(meta, pageWidth - sideMargin * 2);
    pdf.text(lines, pageWidth / 2, y, { align: 'center' });
    y += 5 * lines.length;
  }
  return y + 4;
}

function drawFooter(pdf) {
  const { pageWidth, pageHeight, footerHeight, sideMargin } = LAYOUT_MM;
  const footerTop = pageHeight - footerHeight;

  pdf.setDrawColor(...COLORS_RGB.violet);
  pdf.setLineWidth(0.8);
  pdf.line(sideMargin, footerTop, pageWidth / 2, footerTop);
  pdf.setDrawColor(...COLORS_RGB.pink);
  pdf.line(pageWidth / 2, footerTop, pageWidth - sideMargin, footerTop);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...COLORS_RGB.slate);
  pdf.text(footerContactLine(), pageWidth / 2, footerTop + 8, { align: 'center' });
}

/**
 * Draw letter-pad header + footer on the current page.
 */
export function applyLetterPad(pdf, { logoDataUrl = null, robotDataUrl = null } = {}) {
  drawHeader(pdf, { logoDataUrl, robotDataUrl });
  drawFooter(pdf);
  return {
    contentTop: LAYOUT_MM.contentTop,
    contentBottom: LAYOUT_MM.contentBottom,
    sideMargin: LAYOUT_MM.sideMargin,
    pageW: LAYOUT_MM.pageWidth,
    pageH: LAYOUT_MM.pageHeight,
  };
}

export function stampLetterPadOnAllPages(pdf, assets = {}) {
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    applyLetterPad(pdf, assets);
  }
}

export function withLetterPadPages(pdf, assets, drawContent) {
  applyLetterPad(pdf, assets);
  const ctx = {
    pdf,
    contentTop: LAYOUT_MM.contentTop,
    contentBottom: LAYOUT_MM.contentBottom,
    sideMargin: LAYOUT_MM.sideMargin,
    pageW: LAYOUT_MM.pageWidth,
    pageH: LAYOUT_MM.pageHeight,
    addPage() {
      pdf.addPage();
      applyLetterPad(pdf, assets);
      return LAYOUT_MM.contentTop;
    },
  };
  drawContent(ctx);
  return ctx;
}

export { LAYOUT_MM };
